import React from 'react';
import { motion } from 'framer-motion';
import { X, Calendar, Award, BarChart2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useStreakStats from '../../hooks/UseStreakStatsResult';
import { formatDate } from '../../lib/utils';

interface StreakDetailsModalProps {
    timeRange?: 'week' | 'month' | 'year' | 'all';
    onClose: () => void;
}

export function StreakDetailsModal({
    timeRange = 'all',
    onClose
}: StreakDetailsModalProps) {
    const { t } = useTranslation();
    const { streakData, isLoading, error, resetStreak } = useStreakStats({
        timeRange,
        autoRefresh: false
    });

    const handleResetStreak = async () => {
        if (window.confirm(t('streakStats.confirmReset'))) {
            await resetStreak();
            onClose();
        }
    };

    // Helper function to get completion rate color
    const getColorByPercentage = (percentage) => {
        if (percentage >= 70) return 'bg-emerald-500';
        if (percentage >= 40) return 'bg-blue-500';
        return 'bg-gray-300 dark:bg-gray-600';
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-md w-full max-h-[85vh] overflow-auto"
            >
                {/* Header - Simplified */}
                <div className="flex justify-between items-center p-4 border-b border-gray-100 dark:border-gray-700">
                    <h2 className="text-lg font-medium text-gray-900 dark:text-white">
                        {t('streakStats.detailsTitle')}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-5">
                    {isLoading ? (
                        <div className="flex justify-center items-center h-32">
                            <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                    ) : error ? (
                        <div className="text-red-500 dark:text-red-400 text-center py-4">
                            {error}
                        </div>
                    ) : streakData ? (
                        <>
                            {/* Key Metrics - Simplified */}
                            <div className="grid grid-cols-2 gap-4 mb-6">
                                <div className="flex flex-col items-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                    <Calendar className="w-5 h-5 text-blue-500 mb-1" />
                                    <span className="text-3xl font-bold text-blue-600 dark:text-blue-400">{streakData.currentStreak}</span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">{t('streakStats.currentStreak')}</span>
                                </div>

                                <div className="flex flex-col items-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                                    <Award className="w-5 h-5 text-purple-500 mb-1" />
                                    <span className="text-3xl font-bold text-purple-600 dark:text-purple-400">{streakData.longestStreak}</span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">{t('streakStats.longestStreak')}</span>
                                </div>
                            </div>

                            {/* Last Completed */}
                            {streakData.lastCompletedDate && (
                                <div className="text-sm text-center text-gray-500 dark:text-gray-400 mb-6">
                                    {t('streakStats.lastCompletedOn', { date: formatDate(new Date(streakData.lastCompletedDate)) })}
                                </div>
                            )}

                            {/* Completion Rate By Day - Cleaner visualization */}
                            {streakData.stats && streakData.stats.completionRateByDay && (
                                <div className="mb-6">
                                    <div className="flex items-center mb-3">
                                        <BarChart2 className="w-4 h-4 text-gray-500 mr-1" />
                                        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            {t('streakStats.completionByDay')}
                                        </h3>
                                    </div>

                                    <div className="flex justify-between h-32 items-end">
                                        {streakData.stats.completionRateByDay.map((dayData, index) => (
                                            <div key={index} className="flex flex-col items-center flex-1">
                                                <div className="relative w-full flex justify-center mb-1">
                                                    <div
                                                        className={`w-4/5 rounded-sm ${getColorByPercentage(dayData.percentage)}`}
                                                        style={{ height: `${Math.max((dayData.percentage / 100) * 80, 4)}px` }}
                                                    ></div>
                                                </div>
                                                <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                                    {dayData.day.substring(0, 1)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Recent Streak History - Simplified table */}
                            {streakData.stats && streakData.stats.streakHistory && streakData.stats.streakHistory.length > 0 && (
                                <div className="mb-6">
                                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        {t('streakStats.recentHistory')}
                                    </h3>

                                    <div className="border border-gray-100 dark:border-gray-700 rounded-lg overflow-hidden">
                                        <table className="w-full text-xs">
                                            <thead>
                                                <tr className="bg-gray-50 dark:bg-gray-700">
                                                    <th className="px-3 py-2 text-left text-gray-500 dark:text-gray-400 font-medium">
                                                        {t('streakStats.period')}
                                                    </th>
                                                    <th className="px-3 py-2 text-right text-gray-500 dark:text-gray-400 font-medium">
                                                        {t('streakStats.days')}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {streakData.stats.streakHistory
                                                    .sort((a, b) => new Date(b.end).getTime() - new Date(a.end).getTime())
                                                    .slice(0, 3) // Show only the 3 most recent streaks
                                                    .map((streak, index) => (
                                                        <tr key={index} className="border-t border-gray-100 dark:border-gray-700">
                                                            <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                                                                {formatDate(new Date(streak.end))}
                                                            </td>
                                                            <td className="px-3 py-2 text-right font-medium text-gray-700 dark:text-gray-300">
                                                                {streak.length}
                                                            </td>
                                                        </tr>
                                                    ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Total Completed - Simple stat */}
                            {streakData.stats && typeof streakData.stats.totalTasksCompleted === 'number' && (
                                <div className="flex justify-between items-center py-2 px-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-sm">
                                    <span className="text-gray-600 dark:text-gray-300">{t('streakStats.totalTasksCompleted')}</span>
                                    <span className="font-medium text-gray-800 dark:text-gray-200">
                                        {streakData.stats.totalTasksCompleted}
                                    </span>
                                </div>
                            )}

                        </>
                    ) : (
                        <div className="text-gray-500 dark:text-gray-400 text-center py-6">
                            {t('streakStats.noData')}
                        </div>
                    )}
                </div>

                {/* Footer - Removed separate close button */}
            </motion.div>
        </div>
    );
}

export default StreakDetailsModal;